import {
  boxToPoints,
  isPointInPolygon,
  isPointInPolygonInclusive,
} from "@logic/lib/2d"
import { centerPanelWithinPadding } from "@logic/lib/design"
import type { Face2dComputed } from "@logic/lib/mesh"
import { selectorFamily } from "recoil"

import { enablePanelOverlapValidationState } from "~/containers/LegacyDesigner/state/debug"
import type { PanelValid } from "~/containers/LegacyDesigner/Studio/Elements/Panels/Panel"

import { findPanelState, panelGapState } from "."
import { panelBeingDraggedState } from "./dragging"
import {
  areaIndex,
  findComputedAreaState,
  getInsetAreaFaces,
  computeFace,
  findAreaState,
} from "../areas"
import { areasOfPanelsIndexState } from "../relations"
import type { TransactionOperation } from "../transactions/transactors"
import { readonlyTransactors } from "../transactions/transactors"

export const findPanelIsWarningState: TransactionOperation<string, boolean> = (
  { get },
  panelId,
) => {
  const panel = get(findPanelState(panelId))
  if (!panel) return true
  const gap = get(panelGapState)
  const panelWithoutGap = centerPanelWithinPadding({
    panel,
    gap,
  })
  const panelPoints = boxToPoints({
    ...panel,
    ...panelWithoutGap,
  })
  const myAreaId = get(areasOfPanelsIndexState).from(panelId)
  if (!myAreaId) {
    return true
  }

  let insetAreas: Face2dComputed[]
  try {
    insetAreas = getInsetAreaFaces(readonlyTransactors(get), myAreaId).result
  } catch (e) {
    return true
  }

  const containingArea = insetAreas.find((area) =>
    panelPoints.every((point) => isPointInPolygon(point, area)),
  )
  const panelIsFullyContained = Boolean(containingArea)
  const panelIsWarning = !panelIsFullyContained
  if (panelIsWarning) return true

  const areaIds = get(areaIndex)

  for (const otherAreaId of areaIds) {
    if (otherAreaId === myAreaId) continue
    const otherArea = get(findComputedAreaState(otherAreaId))
    const isInside = panelPoints.some((point) =>
      isPointInPolygon(point, otherArea),
    )
    if (isInside && containingArea) {
      for (const node of containingArea.nodes) {
        if (!isPointInPolygon(node, otherArea)) return false
      }
    }
  }

  const neighborPanelIds = myAreaId
    ? (get(areasOfPanelsIndexState).of(myAreaId) ?? new Set<string>())
    : new Set<string>()
  const panels = Array.from(neighborPanelIds)
    .filter((id) => id !== panelId)
    .map((id) => ({
      id,
      ...get(findPanelState(id)),
    }))

  const panelIsOverlapping = panels.some((otherPanel) => {
    if (otherPanel.id === panelId) return false
    const otherPanelWithoutGap = centerPanelWithinPadding({
      panel: otherPanel,
      gap,
    })
    const otherPanelPoints = boxToPoints({
      ...otherPanel,
      ...otherPanelWithoutGap,
    })

    return otherPanelPoints.some((point) =>
      isPointInPolygonInclusive(point, { nodes: panelPoints }),
    )
  })

  if (panelIsOverlapping) return true

  return false
}

export const getPanelIsError: TransactionOperation<string, boolean> = (
  { get },
  panelId,
) => {
  const panel = get(findPanelState(panelId))
  if (!panel) return true

  const gap = get(panelGapState)
  const panelWithoutGap = centerPanelWithinPadding({
    panel,
    gap,
  })
  const panelPoints = boxToPoints({
    ...panel,
    ...panelWithoutGap,
  })

  const myAreaId = get(areasOfPanelsIndexState).from(panelId)
  if (!myAreaId) {
    return true
  }
  let area: Face2dComputed
  try {
    area = computeFace(get, myAreaId, findAreaState)
  } catch (e) {
    return true
  }

  const panelIsFullyContained = panelPoints.every((point) =>
    isPointInPolygon(point, area),
  )

  const panelIsError = !panelIsFullyContained
  if (panelIsError) {
    return true
  }

  const areaIds = get(areaIndex)
  const otherAreaIds = areaIds.filter((id) => id !== myAreaId)
  const otherAreas = otherAreaIds.map((id) =>
    computeFace(get, id, findAreaState),
  )

  for (const otherArea of otherAreas) {
    const isInside = panelPoints.some((point) =>
      isPointInPolygon(point, otherArea),
    )
    if (isInside) {
      for (const node of area.nodes) {
        if (!isPointInPolygon(node, otherArea)) {
          return false
        }
      }
    }
  }

  return panelIsError
}

export const findPanelValidityState = selectorFamily<PanelValid, string>({
  key: `panelValidity`,
  get:
    (panelId) =>
    ({ get }) => {
      if (get(panelBeingDraggedState) !== null) {
        return `Valid`
      }

      if (!get(enablePanelOverlapValidationState)) {
        return `Valid`
      }

      const isError = getPanelIsError(readonlyTransactors(get), panelId)
      if (isError) return `Error`
      const isWarning = findPanelIsWarningState(
        readonlyTransactors(get),
        panelId,
      )

      return isWarning ? `Warning` : `Valid`
    },
})
